import { gsap } from 'gsap'
import { CustomEase } from 'gsap/CustomEase'

gsap.registerPlugin(CustomEase)
CustomEase.create('slideshow-wipe', '0.6, 0.08, 0.02, 0.99')

function getHomeBannerWrap(target) {
    if (!target) return null

    if (
        typeof target.matches === 'function' &&
        target.matches('[data-slideshow="wrap"]')
    ) {
        return target
    }

    return target.querySelector?.('[data-slideshow="wrap"]') || null
}

function getSlidesAndInner(wrap) {
    const slides = [...wrap.querySelectorAll('[data-slideshow="slide"]')]
    const inner = slides.map(
        (slide) =>
            slide.querySelector('[data-slideshow="parallax"]') ||
            slide.querySelector('img,video') ||
            slide
    )

    return { slides, inner }
}

function setSlidesState(slides, inner, current) {
    slides.forEach((slide, index) => {
        slide.setAttribute('data-index', index)
        slide.classList.toggle('is--current', index === current)
        gsap.set(slide, {
            xPercent: index === current ? 0 : 100,
            zIndex: index === current ? 3 : 1,
            willChange: 'transform'
        })
    })

    inner.forEach((item) => {
        gsap.set(item, { xPercent: 0, willChange: 'transform' })
    })
}

function setHomeBannerCurrentState(target, currentIndex = null) {
    const wrap = getHomeBannerWrap(target)
    if (!wrap) return null

    const { slides, inner } = getSlidesAndInner(wrap)
    if (!slides.length) return null

    const fallbackIndex = slides.length - 1
    const rawIndex = Number.isInteger(currentIndex)
        ? currentIndex
        : fallbackIndex
    const current = Math.max(0, Math.min(rawIndex, slides.length - 1))

    setSlidesState(slides, inner, current)

    return { wrap, slides, inner, current }
}

function createSlideShow(el, options = {}) {
    const setup = setHomeBannerCurrentState(el)
    if (!setup) {
        return null
    }

    const ui = {
        el,
        slides: setup.slides,
        inner: setup.inner
    }

    const length = ui.slides.length
    if (length <= 1) {
        return null
    }

    let current = setup.current
    let animating = false
    let autoplayHandle = null

    const animationDuration = 1
    const autoplayDelay = 2
    const firstAutoplayDelay =
        Number.isFinite(options.firstAutoplayDelay) &&
        options.firstAutoplayDelay > 0
            ? options.firstAutoplayDelay
            : autoplayDelay
    const parallaxAmount = 30
    const innerDurationFactor = 1.2

    let isFirstAutoplay = true

    function scheduleNext() {
        if (autoplayHandle) autoplayHandle.kill()
        const delay = isFirstAutoplay ? firstAutoplayDelay : autoplayDelay
        isFirstAutoplay = false
        autoplayHandle = gsap.delayedCall(delay, () => navigate(1))
    }

    function navigate(direction = 1) {
        if (animating) return
        animating = true

        const previous = current
        current =
            direction === 1
                ? current < length - 1
                    ? current + 1
                    : 0
                : current > 0
                  ? current - 1
                  : length - 1

        const currentSlide = ui.slides[previous]
        const currentInner = ui.inner[previous]
        const upcomingSlide = ui.slides[current]
        const upcomingInner = ui.inner[current]

        gsap.set(currentSlide, { xPercent: 0, zIndex: 3 })
        gsap.set(currentInner, { xPercent: 0 })
        gsap.set(upcomingSlide, { xPercent: 100, zIndex: 4 })
        gsap.set(upcomingInner, { xPercent: 0 })

        gsap.timeline({
            defaults: {
                duration: animationDuration,
                ease: 'slideshow-wipe'
            },
            onStart: () => {
                upcomingSlide.classList.add('is--current')
            },
            onComplete: () => {
                setSlidesState(ui.slides, ui.inner, current)
                animating = false
                scheduleNext()
            }
        })
            .to(currentSlide, { xPercent: -100, force3D: true }, 0)
            .to(
                currentInner,
                {
                    xPercent: parallaxAmount,
                    duration: animationDuration * innerDurationFactor,
                    force3D: true
                },
                0
            )
            .fromTo(
                upcomingSlide,
                { xPercent: 100 },
                { xPercent: 0, force3D: true },
                0
            )
            .fromTo(
                upcomingInner,
                { xPercent: 0 },
                {
                    xPercent: 0,
                    duration: animationDuration * innerDurationFactor,
                    force3D: true
                },
                0
            )
    }

    scheduleNext()

    return {
        destroy() {
            if (autoplayHandle) {
                autoplayHandle.kill()
                autoplayHandle = null
            }

            gsap.killTweensOf(ui.slides)
            gsap.killTweensOf(ui.inner)

            ui.slides.forEach((slide, index) => {
                slide.classList.remove('is--current')
                gsap.set(slide, { clearProps: 'transform,zIndex,willChange' })
                gsap.set(ui.inner[index], {
                    clearProps: 'transform,willChange'
                })
            })
        }
    }
}

const init = (parent) =>
    new Promise((resolve) => {
        if (!parent) return resolve('Homebanner skipped.')

        const wrap = parent.querySelector('[data-slideshow="wrap"]')
        if (!wrap) return resolve('Homebanner skipped.')

        const currentInstance = parent.__homeBannerInstance
        const currentWrap = parent.__homeBannerWrap
        if (currentInstance && currentWrap === wrap) {
            return resolve('Homebanner already loaded.')
        }

        const previousCleanup = parent.__homeBannerCleanup
        if (typeof previousCleanup === 'function') {
            previousCleanup()
        }

        const previousInstance = parent.__homeBannerInstance
        if (previousInstance?.destroy) {
            previousInstance.destroy()
        }

        const fromPageOnceAnimation =
            window.__homeBannerFromOnceAnimation === true

        const instance = createSlideShow(wrap, {
            firstAutoplayDelay: fromPageOnceAnimation ? 5 : 2
        })
        const cleanup = () => {
            if (observer) {
                observer.disconnect()
            }
            if (parent.__homeBannerInstance?.destroy) {
                parent.__homeBannerInstance.destroy()
            }
            delete parent.__homeBannerInstance
            delete parent.__homeBannerWrap
            delete parent.__homeBannerCleanup
        }

        const observer = new MutationObserver(() => {
            if (parent.isConnected) return
            cleanup()
        })

        observer.observe(document.body, {
            childList: true,
            subtree: true
        })

        parent.__homeBannerInstance = instance
        parent.__homeBannerWrap = wrap
        parent.__homeBannerCleanup = cleanup
        return resolve('Homebanner loaded.')
    })

export { init }
export { setHomeBannerCurrentState }
